import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Input,
  InputLabel,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { booleanEqual, Feature, FeatureCollection } from "@turf/turf";
import { Field, GRASS_TYPE } from "model/fieldSlice";
import { useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import { Alert, AlertColor } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNotification } from "app/NotificationProvider";
import {
  ConfirmCancellationDialog,
  DrawerButtonContainer,
  DrawerButtonGroup,
  DrawerForm,
  FullScreenDialog,
  MobileBottomDrawer,
} from "common/components";
import { useTranslation } from "common/locales";
import { booleanValid } from "common/utils";
import DrawingHelpModal from "features/mapDrawTools/DrawingHelpModal";
import { EditorSelectionType } from "features/mapDrawTools/enums";
import {
  ArableField,
  ARABLE_TYPE,
  createArableField,
  createField,
  deleteAllTilesForField,
  deleteTmpFeatures,
  FIELD_TYPE,
  getFieldCount,
  invalidateArableFieldData,
  invalidateFieldData,
  openAfterFieldCreatedDialog,
  selectArableFieldById,
  selectFieldById,
  setEditorIsInTentativeMode,
  setEditorSelectionType,
  setTentativeFeature,
  setTmpFeatures,
  setZoomToGeom,
  TmpFieldDetails,
  updateArableField,
  updateField,
  useAppDispatch,
  useAppSelector,
} from "model";
import { mixpanel } from "../../common/analytics";
import ArableFieldDetailsEditor from "./ArableFieldDetailsEditor";
import FieldDetailsEditor from "./FieldDetailsEditor";
import FieldStats from "./FieldStats";
import FieldTypeRadio from "./FieldTypeRadio";
const PREFIX = "FieldInput";

const classes = {
  formControl: `${PREFIX}-formControl`,
  drawingHintContainer: `${PREFIX}-drawingHintContainer`,
  drawingHint: `${PREFIX}-drawingHint`,
  mobileDialogContent: `${PREFIX}-mobileDialogContent`,
  mobileDialogActions: `${PREFIX}-mobileDialogActions`,
  mobileTopHint: `${PREFIX}-mobileTopHint`,
};

const FormControlStyled = styled(DrawerForm)(({ theme }: { theme: Theme }) => ({
  [`&.${classes.formControl}`]: {
    minWidth: 300,
    paddingBottom: theme.spacing(2),
  },
}));

const MobileDialogContentStyled = styled(DialogContent)(({ theme }) => ({
  [`&.${classes.mobileDialogContent}`]: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // Let the inner div handle scrolling
    position: "relative", // For absolutely positioned children
  },
}));

const DrawingHintContainerStyled = styled(Container)(
  ({ theme }: { theme: Theme }) => ({
    [`&.${classes.drawingHintContainer}`]: {
      bottom: 0,
    },
  })
);

const MobileDialogActionsStyled = styled(DialogActions)(({ theme }) => ({
  [`&.${classes.mobileDialogActions}`]: {
    position: "absolute",
    bottom: "64px", // Position above the navigation bar
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: 0,
    width: "100%",
    boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
    zIndex: 1,
  },
}));

const MobileButtonContainer = styled(DrawerButtonContainer)(({ theme }) => ({
  position: "relative",
  height: "auto",
  minHeight: "auto",
  justifyContent: "center",
}));

const MobileButtonGroup = styled(DrawerButtonGroup)(({ theme }) => ({
  position: "relative",
  right: "auto",
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "flex-end",
  width: "100%",
}));

export const MobileTopHintContainerStyled = styled(Grid)(
  ({ theme }: { theme: Theme }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    pointerEvents: "none",
    padding: `calc(env(safe-area-inset-top) + 20px) 16px 10px 16px`,
    backgroundColor: "transparent",
  })
);

const DrawingHintStyled = styled(Alert)`
  margin: 0 auto;
  max-width: 600px;
  pointer-events: auto;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  white-space: normal;
  .MuiAlert-message {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }
`;

export default function FieldInput() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { path } = useRouteMatch();
  const [fieldNameError, setFieldNameError] = useState(false);
  const [vegetationTypeInputError, setVegetationTypeInputError] =
    useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const [open, setOpen] = useState(false);
  const currentFarmId = useAppSelector((state) => state.farms.currentFarmId!);
  const [isLoading, setIsLoading] = useState(false);
  const fieldCount = useAppSelector(getFieldCount); // TODO: doesn't work when directly hitting /draw-boundary route

  const editorIsInTentativeMode = useAppSelector(
    (state) => state.app.editorIsInTentativeMode
  );

  const editorSelectionType = useAppSelector(
    (state) => state.app.editorSelectionType
  );

  const [tmpField, setTmpField] = useState<TmpFieldDetails>({
    guid: "new",
    name: "",
    farm: currentFarmId,
  });

  const fieldGeom = {
    type: "FeatureCollection",
    features: tmpField?.geom ? tmpField?.geom : [],
  } as FeatureCollection;

  useEffect(() => {
    if (fieldGeom.features.length > 0) {
      dispatch(setZoomToGeom(fieldGeom));
    }
  }, [fieldGeom, dispatch]);

  const { fieldId } = useParams<{ fieldId: string }>();
  const fieldToEdit: Field | undefined = useAppSelector((state) =>
    selectFieldById(state, fieldId)
  );

  const arableFieldToEdit: ArableField | undefined = useAppSelector((state) =>
    selectArableFieldById(state, fieldId)
  );

  const [fieldType, setFieldType] = useState<FIELD_TYPE>(
    fieldToEdit
      ? FIELD_TYPE.GRASS
      : arableFieldToEdit
      ? FIELD_TYPE.ARABLE
      : FIELD_TYPE.GRASS
  );

  const [fieldTypeError, setFieldTypeError] = useState(false);
  const tmpFeatures = useAppSelector((state) => state.app.tmpFeatures);
  const tentativeFeature = useAppSelector(
    (state) => state.app.tentativeFeature
  );
  // there should only ever be 1 field boundary, so taking the first element that's a polygon, or undefined if there are no matches ([][0] is undefined)
  const tmpFieldGeom =
    tmpFeatures.filter((f) => f.geometry?.type === "Polygon")[0] ||
    tentativeFeature;
  useEffect(() => {
    const curField: Field | ArableField | undefined =
      fieldToEdit ?? arableFieldToEdit;
    if (curField) {
      setTmpField(curField);
      dispatch(setTmpFeatures([curField.geom])); // not cleaned up properly after save
      dispatch(
        setZoomToGeom({
          type: "FeatureCollection",
          features: [curField.geom],
        })
      );
    } else {
      dispatch(setTmpFeatures([])); // reset when creating a new field
    }
  }, [fieldToEdit, arableFieldToEdit, dispatch]);

  const handleFieldNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTmpField({ ...tmpField, name: e.target.value });
    setFieldNameError(false);
  };

  const handleFieldTypeChange = (value?: FIELD_TYPE) => {
    if (value) {
      setFieldType(value);
    } else {
      setFieldTypeError(true);
    }
  };

  const handleSave = async () => {
    try {
      const fieldNameSet = !!tmpField.name;
      setFieldNameError(!fieldNameSet);
      if (fieldType === FIELD_TYPE.GRASS) {
        const grassTypeInputError = !tmpField.grassType;
        setVegetationTypeInputError(grassTypeInputError);
        if (grassTypeInputError) {
          return;
        }
      }

      if (fieldType === FIELD_TYPE.ARABLE) {
        const arableTypeInputError = !tmpField.arableType;
        setVegetationTypeInputError(arableTypeInputError);
        if (arableTypeInputError) {
          return;
        }
      }
      if (!fieldNameSet) {
        return;
      }
      if (fieldTypeError) {
        return;
      }

      if (!isGeomValid(tmpFieldGeom)) {
        if (!tentativeFeature || !isGeomValid(tentativeFeature)) {
          return notify(t("fieldinput.boundary.error"), "error");
        } else {
          // defensive logic
          setIsLoading(true);
          dispatch(setTmpFeatures([tentativeFeature] as any));
          dispatch(setTentativeFeature(null)); // TODO: this should belong to the editor
          dispatch(setEditorSelectionType(EditorSelectionType.NONE)); // reset editor
          dispatch(setEditorIsInTentativeMode(false));
          await create(tentativeFeature as any);
        }
      }
      setIsLoading(true);

      // TODO: this should belong to the editor
      dispatch(setEditorSelectionType(EditorSelectionType.NONE)); // reset editor
      dispatch(setEditorIsInTentativeMode(false));

      if (tmpField.guid === "new" && currentFarmId) {
        await create(tmpFieldGeom);
      } else {
        await update();
      }
    } catch (e: any) {
      notify(t("fieldinput.save.error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationClose = () => {
    setOpen(false);

    // reset editor
    dispatch(deleteTmpFeatures());
    dispatch(setTentativeFeature(null));
    dispatch(setEditorSelectionType(EditorSelectionType.NONE));
    dispatch(setEditorIsInTentativeMode(false));
  };

  const handleCancel = () => {
    setOpen(true);
  };

  const { severity, hintId } = getHint(
    editorIsInTentativeMode,
    editorSelectionType,
    tmpFeatures,
    tentativeFeature
  );

  const enterDetailsMatch = useRouteMatch(`${path}/enter-details`);
  const drawBoundaryMatch = useRouteMatch(`${path}/draw-boundary`);

  if (isMobile) {
    // Show enter-details view
    if (enterDetailsMatch) {
      return (
        <FullScreenDialog>
          <DialogTitle>{t("fieldinput.title.mobile")}</DialogTitle>
          <MobileDialogContentStyled className={classes.mobileDialogContent}>
            <FormControlStyled className={classes.formControl}>
              <InputLabel htmlFor="input-name" variant="standard">
                {t("edit.fieldname.label")}
              </InputLabel>
              <Input
                required
                error={fieldNameError}
                id="input-name"
                value={tmpField.name}
                onChange={handleFieldNameChange}
                autoFocus={true}
              />
            </FormControlStyled>

            <Container sx={{ alignContent: "left", paddingTop: "20px" }}>
              <FormControlStyled className={classes.formControl}>
                <FieldTypeRadio
                  disabled={!!(fieldToEdit || arableFieldToEdit)}
                  onChange={handleFieldTypeChange}
                  currentFieldType={fieldType}
                  fieldTypeInputError={fieldTypeError}
                  label={"fieldinput.fieldType.text"}
                />
              </FormControlStyled>
            </Container>
            {fieldType === FIELD_TYPE.GRASS && (
              <FieldDetailsEditor
                field={tmpField}
                handleAssignedToChange={handleAssignedToChange}
                handleGrassTypeChange={handleGrassTypeChange}
                fieldNameError={fieldNameError}
                currentGrassType={fieldToEdit?.grassType ?? undefined}
                grassTypeInputError={vegetationTypeInputError}
              />
            )}
            {fieldType === FIELD_TYPE.ARABLE && (
              <ArableFieldDetailsEditor
                field={tmpField}
                handleArableTypeChange={handleArableTypeChange}
                fieldNameError={fieldNameError}
                currentArableType={arableFieldToEdit?.arableType ?? undefined}
                arableTypeInputError={vegetationTypeInputError}
              />
            )}
          </MobileDialogContentStyled>
          <MobileDialogActionsStyled className={classes.mobileDialogActions}>
            <MobileButtonContainer>
              <MobileButtonGroup>
                <Button color="secondary" onClick={handleCancel}>
                  {t("edit.button.cancel")}
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={
                    !tmpField.name ||
                    (fieldType === FIELD_TYPE.GRASS && !tmpField.grassType) ||
                    (fieldType === FIELD_TYPE.ARABLE && !tmpField.arableType)
                  }
                  onClick={() => {
                    if (!tmpField.name) {
                      setFieldNameError(true);
                      return;
                    }

                    // Validate vegetation type based on field type
                    if (fieldType === FIELD_TYPE.GRASS && !tmpField.grassType) {
                      setVegetationTypeInputError(true);
                      return;
                    }

                    if (
                      fieldType === FIELD_TYPE.ARABLE &&
                      !tmpField.arableType
                    ) {
                      setVegetationTypeInputError(true);
                      return;
                    }

                    // All validations passed, proceed to next step
                    setFieldNameError(false);
                    setVegetationTypeInputError(false);
                    history.push("draw-boundary");
                  }}
                >
                  {t("edit.button.next")}
                </Button>
              </MobileButtonGroup>
              <ConfirmCancellationDialog
                open={open}
                onClose={handleConfirmationClose}
              />
            </MobileButtonContainer>
          </MobileDialogActionsStyled>
        </FullScreenDialog>
      );
    }

    // Show draw-boundary view
    if (drawBoundaryMatch) {
      return (
        <Dialog
          open={true}
          fullScreen
          style={{ pointerEvents: "none" }} // Make the entire dialog non-interactive
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              pointerEvents: "none",
              overflow: "visible", // Ensure content isn't clipped
            },
          }}
          BackdropProps={{
            style: {
              backgroundColor: "transparent",
              pointerEvents: "none", // Ensure backdrop doesn't block
            },
          }}
          hideBackdrop={true} // Completely remove backdrop
        >
          <div style={{ whiteSpace: "normal" }}>
            <DrawingHelpModal
              isOpen={snackbarOpen && fieldCount === 0 && !tmpFieldGeom}
              handleClose={handleSnackbarClose}
            />
          </div>
          <MobileTopHintContainerStyled
            className={classes.mobileTopHint}
            container
            style={{ pointerEvents: "auto" }} // Re-enable pointer events for the hint
          >
            <Grid item xs={12}>
              <DrawingHintStyled
                className={classes.drawingHint}
                severity={severity}
              >
                {t(hintId)}
              </DrawingHintStyled>
            </Grid>
          </MobileTopHintContainerStyled>
          <MobileBottomDrawer container style={{ pointerEvents: "auto" }}>
            {" "}
            {/* Re-enable for buttons */}
            <Grid item xs={12}>
              <FieldStats geom={tmpFieldGeom} direction="row" />
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end">
              <Button
                color="secondary"
                onClick={() => history.push("enter-details")}
              >
                {t("edit.button.back")}
              </Button>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isLoading || !isGeomValid(tmpFieldGeom)}
              >
                {t("edit.button.save")}
              </Button>
            </Grid>
          </MobileBottomDrawer>
        </Dialog>
      );
    }

    // Default fallback - shouldn't happen but just in case
    return null;
  } else {
    return (
      <>
        <Container>
          <Container>
            <FormControlStyled className={classes.formControl}>
              <InputLabel htmlFor="input-name" variant="standard">
                {t("edit.fieldname.label")}
              </InputLabel>
              <Input
                required
                error={fieldNameError}
                id="input-name"
                value={tmpField.name}
                onChange={handleFieldNameChange}
                autoFocus={true}
              />
            </FormControlStyled>
          </Container>

          <Container sx={{ alignContent: "left" }}>
            <FormControlStyled className={classes.formControl}>
              <FieldTypeRadio
                disabled={!!(fieldToEdit || arableFieldToEdit)}
                onChange={handleFieldTypeChange}
                currentFieldType={fieldType}
                fieldTypeInputError={fieldTypeError}
                label={"fieldinput.fieldType.text"}
              />
            </FormControlStyled>
          </Container>

          {fieldType === FIELD_TYPE.GRASS && (
            <FieldDetailsEditor
              field={tmpField}
              handleAssignedToChange={handleAssignedToChange}
              handleGrassTypeChange={handleGrassTypeChange}
              fieldNameError={fieldNameError}
              currentGrassType={fieldToEdit?.grassType ?? undefined}
              grassTypeInputError={vegetationTypeInputError}
            />
          )}
          {fieldType === FIELD_TYPE.ARABLE && (
            <ArableFieldDetailsEditor
              field={tmpField}
              handleArableTypeChange={handleArableTypeChange}
              fieldNameError={fieldNameError}
              currentArableType={arableFieldToEdit?.arableType ?? undefined}
              arableTypeInputError={vegetationTypeInputError}
            />
          )}
        </Container>
        <Container>
          <FieldStats geom={tmpFieldGeom} direction="column" />
        </Container>
        <Divider />
        <DrawerButtonContainer>
          <Button color="secondary" onClick={handleCancel} disabled={isLoading}>
            {t("edit.button.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={isLoading || !isGeomValid(tmpFieldGeom)}
          >
            {t("edit.button.save")}
          </Button>
          <ConfirmCancellationDialog
            open={open}
            onClose={handleConfirmationClose}
          />
        </DrawerButtonContainer>
        <DrawingHintContainerStyled className={classes.drawingHintContainer}>
          <DrawingHintStyled
            className={classes.drawingHint}
            severity={severity}
          >
            {t(hintId)}
          </DrawingHintStyled>
        </DrawingHintContainerStyled>
      </>
    );
  }

  function handleAssignedToChange(value?: string) {
    setTmpField({ ...tmpField, animalGroup: value });
  }

  function handleGrassTypeChange(value?: GRASS_TYPE) {
    setTmpField({ ...tmpField, grassType: value });
    setVegetationTypeInputError(!value);
  }

  function handleArableTypeChange(value?: ARABLE_TYPE) {
    setTmpField({ ...tmpField, arableType: value });
    setVegetationTypeInputError(!value);
  }

  async function create(geom: Feature) {
    let newField;
    if (fieldType === FIELD_TYPE.GRASS) {
      tmpField.arableType = undefined;
      newField = await dispatch(
        createField({
          field: {
            ...tmpField,
            dryMatterNow: 0,
            farm: currentFarmId,
            geom,
            area: 0,
            tiles: [],
          },
        })
      ).then(unwrapResult);
      dispatch(invalidateFieldData(newField.guid));
    } else {
      tmpField.grassType = undefined;
      newField = await dispatch(
        createArableField({
          arableField: {
            ...tmpField,
            arableType: tmpField.arableType ?? ARABLE_TYPE.BARLEY,
            farm: currentFarmId,
            geom,
            area: 0,
            tiles: [],
          },
        })
      ).then(unwrapResult);
      dispatch(invalidateArableFieldData(newField.guid));
    }
    notify(t("fieldInput.add.success", { fieldName: tmpField.name }));
    mixpanel.track("FieldAdd successful");
    dispatch(deleteTmpFeatures());
    dispatch(setTentativeFeature(null)); // reset tentative feature
    dispatch(openAfterFieldCreatedDialog());
    if (fieldCount < 1) {
      // show congrats dialog
      mixpanel.track("AfterFieldCreatedDialog after 1st field open");
    } else {
      // show general "want another field?" dialog
      mixpanel.track("AfterFieldCreatedDialog after nst field open");
    }
    history.push(`/field/${newField.guid}`);
  }

  async function update(): Promise<void> {
    const geomChanged = !(
      (fieldToEdit && booleanEqual(tmpFieldGeom, fieldToEdit.geom)) ||
      (arableFieldToEdit && booleanEqual(tmpFieldGeom, arableFieldToEdit.geom))
    );
    if (geomChanged) {
      dispatch(deleteAllTilesForField(tmpField.guid));
    }

    if (fieldType === FIELD_TYPE.GRASS) {
      await dispatch(
        updateField({
          ...tmpField,
          dryMatterNow: (!geomChanged && fieldToEdit?.dryMatterNow) || 0,
          geom: tmpFieldGeom,
          area: (!geomChanged && fieldToEdit?.area) || 0,
        })
      );

      if (geomChanged) {
        dispatch(invalidateFieldData(tmpField.guid));
      }
    } else {
      await dispatch(
        updateArableField({
          guid: tmpField.guid,
          name: tmpField.name,
          farm: tmpField.farm,
          arableType: tmpField.arableType ?? ARABLE_TYPE.BARLEY,
          geom: tmpFieldGeom,
          area: (!geomChanged && fieldToEdit?.area) || 0,
        })
      );
      if (geomChanged) {
        dispatch(invalidateArableFieldData(tmpField.guid));
      }
    }

    notify(t("fieldInput.edit.success", { fieldName: tmpField.name }));
    mixpanel.track("FieldEdit successfull");
    dispatch(deleteTmpFeatures());
    history.push(`/field/${tmpField.guid}`);
  }

  function handleSnackbarClose() {
    setSnackbarOpen(false);
  }
}

function isGeomValid(geom: Feature<any>) {
  return geom && booleanValid(geom);
}

function getHint(
  editorIsInTentativeMode: boolean,
  editorSelectionType: EditorSelectionType,
  tmpFeatures: Array<Feature>,
  tentativeFeature: Feature | null
): { hintId: string; severity: AlertColor } {
  let severity: AlertColor = "info";
  let hintId = "map.clickOnPencilButton.hint"; // edit mode, no feature exists
  // TODO: simplify it
  if (editorIsInTentativeMode) {
    if (!tentativeFeature) {
      // not enough points
      hintId = "map.provideThreePoints.hint";
    } else if (isGeomValid(tentativeFeature)) {
      // valid tentative feature
      hintId = "map.doubleClickToFinish.hint";
    } else {
      // invalid geometry
      hintId = "map.provideValidGeometry.hint";
      severity = "error";
    }
  } else {
    // in edit mode
    if (tmpFeatures.length > 0) {
      // feature exists
      if (editorSelectionType === EditorSelectionType.HANDLES) {
        // selected handles, tell them how to delete
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.editingField.hint"; // not using "map.deleteHandles.hint" for now because of the bug in react-draw-map-gl not providing correct handles
        } else {
          // invalid geometry
          hintId = "map.provideValidGeometry.hint";
          severity = "error";
        }
      } else if (editorSelectionType === EditorSelectionType.FEATURE) {
        // feature selected
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.editingField.hint"; // valid geometry, can select points and edit, delete
        } else {
          // invalid geometry
          hintId = "map.provideValidGeometry.hint";
          severity = "error";
        }
      } else {
        // no feature selected (pencil button inactive)
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.clickOnPencilButtonToEdit.hint"; // valid geometry, can activate pencil and edit
        } else {
          hintId = "map.provideValidGeometry.hint"; // invalid geometry
          severity = "error";
        }
      }
    }
  }

  return {
    severity,
    hintId,
  };
}
